import {PriorityConfig} from '@aboutyou/data-fetcher';
import {Grid, useScrollToTopOnUnload} from '@aboutyou/ui-components';
import React from 'react';
import styled from 'styled-components';
import {useActivatePreselectedBestCouponABTest} from '../../../features/Coupons/useActivatePreselectedBestCouponABTest';
import {ProductStreamEmpty} from '../../../features/ProductStream/ProductStreamEmpty';
import {
  StreamTileList,
  StreamTileListProps,
} from '../../../features/ProductStream/StreamTileList';
import {TilesByPage} from '../../../features/ProductStream/TilesByPage';
import {StreamPaddingCSS} from '../../../features/ProductStream/helpers';
import {usePotentialFilters} from '../../../features/ProductStream/utils';
import {
  LoadMoreComponentDisplayConfig,
  ProductStreamTokenPagination,
} from '../../../features/ProductStreamPagination/ProductStreamTokenPagination';
import {useShallowRenderingState} from '../../../features/ProductTile/utils';
import {useIsInitialStreamRequestEmpty} from '../../../features/TokenBasedStreamPages/useIsInitialStreamRequestEmpty';
import {useTokenBasedStreamPages} from '../../../features/TokenBasedStreamPages/useTokenBasedStreamPages';
import {usePreventProductTilesSwipeAnimation} from '../../CouponLandingPage/ProductStream/usePreventProductTilesSwipeAnimation';
import {useBrandPageStream} from './hooks/useBrandPageStream';
import {useBrandPageStreamNext} from './hooks/useBrandPageStreamNext';

const HISTORY_STATE_LAST_VISIBLE_ITEMS_KEY =
  'brand_shop_stream_last_visible_items';

interface BrandPageStreamProps extends StreamTileListProps, PriorityConfig {
  loadMoreComponentDisplayConfig: LoadMoreComponentDisplayConfig;
}

export const BrandPageStream: React.FC<BrandPageStreamProps> = ({
  gridColumns,
  onClickAddCta,
  productTileGalleryMode,
  enableImageGalleryAnimation,
  ProductTileComponent,
  layoutConfig,
  constrainStoryTilesToViewportBoundary,
  loadMoreComponentDisplayConfig,
  enableOutfitSlider,
  interactiveHoverMode,
}) => {
  const {pages, isNextPageReady, mountNextPage} = useTokenBasedStreamPages({
    uniqueKey: 'brand-page',
    request: useBrandPageStream,
    nextRequest: useBrandPageStreamNext,
  });

  const preventProductTilesSwipeAnimation =
    usePreventProductTilesSwipeAnimation();

  const {resetAllFilters} = usePotentialFilters();

  const shallowRenderingState = useShallowRenderingState({
    uniqueKey: HISTORY_STATE_LAST_VISIBLE_ITEMS_KEY,
  });

  const isInitialRequestEmpty =
    useIsInitialStreamRequestEmpty(useBrandPageStream);

  useScrollToTopOnUnload();

  useActivatePreselectedBestCouponABTest();

  return (
    <section>
      <StyledGrid
        columns={gridColumns}
        enableEnhancedTileRendering
        uniqueKey="brandShopStream"
      >
        {pages.map((page, i) => (
          <TilesByPage
            key={i}
            page={page}
            StreamTileList={StreamTileList}
            gridColumns={gridColumns}
            shallowRenderingState={shallowRenderingState}
            onClickAddCta={onClickAddCta}
            disableImagePreloading
            productTileGalleryMode={productTileGalleryMode}
            enableImageGalleryAnimation={
              enableImageGalleryAnimation && !preventProductTilesSwipeAnimation
            }
            ProductTileComponent={ProductTileComponent}
            layoutConfig={layoutConfig}
            enableOutfitSlider={enableOutfitSlider}
            constrainStoryTilesToViewportBoundary={
              constrainStoryTilesToViewportBoundary
            }
            interactiveHoverMode={interactiveHoverMode}
          />
        ))}
      </StyledGrid>
      {isNextPageReady && (
        <ProductStreamTokenPagination
          mountNextPage={mountNextPage}
          loadMoreComponentDisplayConfig={loadMoreComponentDisplayConfig}
        />
      )}
      {isInitialRequestEmpty && (
        <ProductStreamEmpty onFilterClear={resetAllFilters} />
      )}
    </section>
  );
};

const StyledGrid = styled(Grid)`
  ${StreamPaddingCSS}
`;
